import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtINR, fmtDate, API } from "../lib/apiClient";
import { DOC_TYPES } from "../lib/constants";
import { X, FilePdf, ArrowSquareOut, PencilSimple, Printer, BookOpen, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PdfPreviewModal, FilePreview } from "./PdfPreviewModal";
import { RescanBillModal } from "./RescanBillModal";
import { LedgerPostingPicker } from "./LedgerFormModal";

const TITLE = { sales: "Sales Voucher", purchase: "Purchase Voucher", receipt: "Receipt Voucher", payment: "Payment Voucher",
  contra: "Contra Voucher", journal: "Journal Voucher", credit_note: "Credit Note Voucher" };

// Party ledger of a voucher: purchase/receipt/credit-note → credited side, sales/payment → debited side.
export function partyLine(v) {
  if (!v?.lines?.length) return null;
  const side = ["purchase", "receipt", "credit_note"].includes(v.voucher_type) ? "cr" : "dr";
  return v.lines.find(l => l[side] > 0) || null;
}

export function VoucherDetailModal({ voucherId, onClose }) {
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [showFile, setShowFile] = useState(true);
  const [rescanOpen, setRescanOpen] = useState(false);
  const [heads, setHeads] = useState(null); // { dr_ledger_id, cr_ledger_id } while editing
  const [savingHeads, setSavingHeads] = useState(false);
  const saveHeads = async () => {
    setSavingHeads(true);
    try {
      const { data } = await api.patch(`/accounting/vouchers/${encodeURIComponent(voucherId)}/heads`, heads);
      setV(data); setHeads(null); toast.success("Ledger heads updated — voucher re-posted");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setSavingHeads(false); }
  };
  const closePdf = useCallback(() => setPdf(null), []);
  const load = useCallback(() => api.get(`/accounting/vouchers/${encodeURIComponent(voucherId)}`, { timeout: 60000 })
    .then(r => setV(r.data))
    .catch(e => { toast.error(formatApiError(e?.response?.data?.detail) || "Could not load voucher"); onClose(); }), [voucherId, onClose]);
  useEffect(() => { if (!voucherId) return; setV(null); load(); }, [voucherId, load]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !pdf && !rescanOpen) { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [onClose, pdf, rescanOpen]);
  const openLedger = (ledgerId) => { onClose(); nav(`/accounting?tab=Ledgers&ledger=${ledgerId}`); };
  if (!voucherId) return null;
  const src = v?.source_detail;
  const hasFile = src?.kind === "inward_bill" && !!src.file_url;
  const fileUrl = hasFile ? `${API}${src.file_url.replace(/^\/api/, "")}` : null;
  const party = partyLine(v);
  const canEditHeads = v?.source === "system" && v?.id && ["sales", "purchase"].includes(v.voucher_type) && src && ["document", "inward_bill"].includes(src.kind);
  // Current heads: purchase → Dr = purchase a/c line, Cr = vendor line; sales → Dr = customer line, Cr = sales a/c line (skip GST/round-off lines)
  const currentHead = (side) => {
    const tax = /^(Input|Output) [CSI]GST$|^Round Off$/;
    const l = (v?.lines || []).find(x => (side === "dr" ? x.dr > 0 : x.cr > 0) && !tax.test(x.ledger_name));
    return l?.ledger_id || "";
  };
  const openPdf = () => setPdf({
    src: `${API}/accounting/vouchers/${encodeURIComponent(v.id || v.source_key)}/voucher.pdf`,
    filename: `${v.voucher_type}-voucher-${(v.number || v.voucher_no || "").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`,
    title: `${TITLE[v.voucher_type] || "Voucher"} · ${v.number || "#" + (v.voucher_no || "")}`,
  });
  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="voucher-modal">
      {pdf && <div onClick={e => e.stopPropagation()}><PdfPreviewModal src={pdf.src} filename={pdf.filename} title={pdf.title} onClose={closePdf} /></div>}
      {rescanOpen && src && <div onClick={e => e.stopPropagation()}><RescanBillModal bill={{ id: src.id, bill_no: src.number, vendor_name: src.party, file_id: src.file_id }} onClose={() => setRescanOpen(false)} onApplied={() => { setRescanOpen(false); load(); }} /></div>}
      <div className={`bg-white rounded-lg w-full ${hasFile && showFile ? "max-w-7xl" : "max-w-3xl"} shadow-2xl max-h-[94vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-border sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="font-display font-semibold" data-testid="voucher-title">{v ? TITLE[v.voucher_type] || "Voucher" : "Loading…"}</div>
            {v && <div className="text-xs text-ink-muted truncate">Vch No <b className="font-mono">{v.voucher_no || "—"}</b> · {fmtDate(v.date)} · Ref <span className="font-mono">{v.number || "—"}</span>{v.source !== "system" && <span className="ml-1 status-pill status-info">{v.source === "manual" ? "manual" : "tally"}</span>}</div>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {hasFile && <button onClick={() => setShowFile(s => !s)} className="btn-secondary inline-flex items-center gap-1 text-xs hidden lg:inline-flex" data-testid="voucher-toggle-file"><FilePdf size={14} /> {showFile ? "Hide bill" : "Show bill"}</button>}
            {v && <button onClick={openPdf} className="btn-primary inline-flex items-center gap-1 text-xs" data-testid="voucher-pdf-btn"><Printer size={14} weight="bold" /> PDF</button>}
            <button onClick={onClose} className="btn-ghost" data-testid="voucher-close"><X size={16} /></button>
          </div>
        </div>
        {v && (
          <div className={`p-5 grid gap-5 ${hasFile && showFile ? "lg:grid-cols-2" : "grid-cols-1"}`}>
            {hasFile && showFile && (
              <div className="order-2 lg:order-1 rounded-md border border-border bg-surface-muted p-2 lg:max-h-[80vh] lg:overflow-y-auto" data-testid="voucher-bill-preview">
                <div className="flex items-center justify-between px-1 pb-2 flex-wrap gap-1">
                  <div className="label-overline">Uploaded bill</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setRescanOpen(true)} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="voucher-rescan-btn"><Robot size={13} /> Re-read with AI</button>
                    <a href={fileUrl} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-1 text-xs" data-testid="voucher-bill-file"><ArrowSquareOut size={13} /> Open original</a>
                  </div>
                </div>
                <FilePreview url={fileUrl} mime={src.file_mime} alt={`Bill ${src.number}`} />
              </div>
            )}
            <div className="order-1 lg:order-2 space-y-5 text-sm min-w-0">
              <VoucherLines lines={v.lines} total={v.total} onLedger={openLedger} />
              {canEditHeads && !heads && (
                <button onClick={() => setHeads({ dr_ledger_id: currentHead("dr"), cr_ledger_id: currentHead("cr") })} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="voucher-edit-heads">
                  <PencilSimple size={13} /> Change Dr / Cr ledger heads
                </button>
              )}
              {heads && (
                <div className="space-y-2" data-testid="voucher-heads-editor">
                  <LedgerPostingPicker testPrefix="vh" mode={v.voucher_type === "sales" ? "sales" : "purchase"} drId={heads.dr_ledger_id} crId={heads.cr_ledger_id}
                    partyName={src?.party} purchaseType={src?.purchase_type} onChange={(patch) => setHeads(h => ({ ...h, ...patch }))} />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setHeads(null)} className="btn-secondary text-xs" data-testid="voucher-heads-cancel">Cancel</button>
                    <button onClick={saveHeads} disabled={savingHeads} className="btn-primary text-xs" data-testid="voucher-heads-save">{savingHeads ? "Re-posting…" : "Save & re-post voucher"}</button>
                  </div>
                </div>
              )}
              {v.narration && <div className="text-xs"><span className="label-overline">Narration</span><div className="mt-1">{v.narration}</div></div>}
              {src && (src.kind === "inward_bill" || src.kind === "document") && <SourceItems src={src} partyLedger={party} onLedger={openLedger} />}
              {src && (src.kind === "receipt" || src.kind === "payment") && <MoneyRows src={src} />}
              {v.source === "system" && !src && <div className="text-xs text-ink-muted">Source record no longer exists.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VoucherLines({ lines, total, onLedger }) {
  return (
    <div className="table-wrapper" data-testid="voucher-lines">
      <table className="table-base">
        <thead><tr><th>Particulars (Ledger) <span className="normal-case tracking-normal font-normal text-[10px]">· click to open ledger</span></th><th className="text-right">Debit (₹)</th><th className="text-right">Credit (₹)</th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={`${l.ledger_id}-${i}`}>
              <td className={l.dr > 0 ? "font-semibold" : "pl-8"}>{l.dr > 0 ? "Dr " : "Cr "}
                <button onClick={() => onLedger(l.ledger_id)} className="underline decoration-dotted underline-offset-2 hover:text-brand" data-testid={`voucher-ledger-${l.ledger_id}`} title="Open ledger statement">{l.ledger_name}</button>
              </td>
              <td className="text-right tabular">{l.dr > 0 ? fmtINR(l.dr) : ""}</td>
              <td className="text-right tabular">{l.cr > 0 ? fmtINR(l.cr) : ""}</td>
            </tr>
          ))}
          {lines.length === 0 && <tr><td colSpan={3} className="text-center text-xs text-ink-muted py-3">No ledger posting for this record.</td></tr>}
          <tr className="bg-surface-muted font-bold"><td>Total</td><td className="text-right tabular">{fmtINR(total)}</td><td className="text-right tabular">{fmtINR(total)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function SourceItems({ src, partyLedger, onLedger }) {
  const isBill = src.kind === "inward_bill";
  const docPath = !isBill && DOC_TYPES[src.doc_type]?.path;
  return (
    <div className="space-y-3" data-testid="voucher-source">
      <div className="flex items-start justify-between flex-wrap gap-2 border border-dashed border-border rounded-md p-3">
        <div>
          <div className="label-overline">{isBill ? "Purchase Bill" : DOC_TYPES[src.doc_type]?.label || "Document"} <span className="font-mono normal-case">{src.number}</span></div>
          <div className="text-xs mt-1">
            {partyLedger
              ? <button onClick={() => onLedger(partyLedger.ledger_id)} className="font-bold inline-flex items-center gap-1 hover:text-brand underline decoration-dotted underline-offset-2" data-testid="voucher-party-ledger" title={`Open ${isBill ? "vendor" : "customer"} ledger statement`}><BookOpen size={13} /> {src.party || "—"}</button>
              : <b>{src.party || "—"}</b>}
            {src.party_gstin && <span className="font-mono text-ink-muted ml-2">{src.party_gstin}</span>} · {fmtDate(src.date)}</div>
          {isBill && <div className="text-[11px] text-ink-muted mt-0.5">ITC {src.itc_type || "OTH"}{src.purchase_type && ` · ${src.purchase_type.replace("_", "-")}`}{src.notes && ` · ${src.notes}`}</div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {isBill && <Link to={`/inward-bills?edit=${src.id}`} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="voucher-edit-bill"><PencilSimple size={14} /> Edit bill & items</Link>}
          {docPath && <Link to={`/${docPath}/${src.id}`} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="voucher-open-doc"><ArrowSquareOut size={14} /> Open {DOC_TYPES[src.doc_type]?.label}</Link>}
        </div>
      </div>
      <div className="table-wrapper">
        <table className="table-base" data-testid="voucher-items">
          <thead><tr><th>#</th><th>Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Rate (₹)</th><th className="text-right">GST %</th><th className="text-right">Amount (₹)</th></tr></thead>
          <tbody>
            {src.items.map((it, i) => (
              <tr key={`${it.name}-${it.hsn}-${i}`} data-testid={`voucher-item-${i}`}>
                <td className="text-xs text-ink-muted">{i + 1}</td>
                <td className="font-semibold">{it.name}</td>
                <td className="font-mono text-xs">{it.hsn || "—"}</td>
                <td className="text-right tabular">{it.qty}{it.unit ? ` ${it.unit}` : ""}</td>
                <td className="text-right tabular">{fmtINR(it.rate)}</td>
                <td className="text-right tabular">{it.gst_rate ? `${it.gst_rate}%` : "—"}</td>
                <td className="text-right tabular font-semibold">{fmtINR(it.amount)}</td>
              </tr>
            ))}
            {src.items.length === 0 && <tr><td colSpan={7} className="text-center py-5 text-ink-muted">No item lines were captured for this {isBill ? "bill" : "document"}.</td></tr>}
          </tbody>
          <tfoot>
            <tr className="bg-surface-muted"><td colSpan={6} className="text-right text-xs">Taxable</td><td className="text-right tabular">{fmtINR(src.taxable)}</td></tr>
            {Number(src.cgst) > 0 && <tr><td colSpan={6} className="text-right text-xs">CGST</td><td className="text-right tabular">{fmtINR(src.cgst)}</td></tr>}
            {Number(src.sgst) > 0 && <tr><td colSpan={6} className="text-right text-xs">SGST</td><td className="text-right tabular">{fmtINR(src.sgst)}</td></tr>}
            {Number(src.igst) > 0 && <tr><td colSpan={6} className="text-right text-xs">IGST</td><td className="text-right tabular">{fmtINR(src.igst)}</td></tr>}
            {Number(src.cess) > 0 && <tr><td colSpan={6} className="text-right text-xs">Cess</td><td className="text-right tabular">{fmtINR(src.cess)}</td></tr>}
            {Math.abs(Number(src.round_off) || 0) >= 0.01 && <tr><td colSpan={6} className="text-right text-xs">Round off</td><td className="text-right tabular">{fmtINR(src.round_off)}</td></tr>}
            <tr className="font-bold"><td colSpan={6} className="text-right">Bill Total</td><td className="text-right tabular" data-testid="voucher-bill-total">{fmtINR(src.total)}</td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function MoneyRows({ src }) {
  return (
    <div className="table-wrapper" data-testid="voucher-money-rows">
      <table className="table-base">
        <thead><tr><th>Against Bill</th><th>Date</th><th>Mode</th><th>Reference</th><th className="text-right">Amount (₹)</th></tr></thead>
        <tbody>
          {src.rows.map((r, i) => (
            <tr key={`${r.number}-${r.date}-${i}`}><td className="font-mono">{r.number || "—"}{r.party && <span className="text-xs text-ink-muted ml-2">{r.party}</span>}</td><td className="text-xs">{fmtDate(r.date)}</td><td>{r.mode}</td><td className="text-xs">{r.reference || "—"}{r.notes && <div className="text-[10px] text-ink-muted">{r.notes}</div>}</td><td className="text-right tabular font-semibold">{fmtINR(r.amount)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
